import { v4 as uuid } from 'uuid';
import { t } from 'i18next';

import { BaseNotification, NotificationType } from '@/modules/Notifications/helpers/types';
import { getConfig } from '@/modules/common/helpers/config';
import { BaseConnector } from '@/modules/api/BaseConnector';
import { API_TIMEOUTS } from '../constant';

import { IErrorMessageServiceConnector } from './IErrorMessageServiceConnector';

type ErrorMessageServiceResponse = {
  severity: string;
  code: string;
  readMoreUrl?: string;
  descriptions: Array<{
    title?: string;
    description?: string;
    solution?: string;
  }>;
};

const toNotificationType = (severity: string): NotificationType => {
  const normalized = severity.toLowerCase();

  if (normalized === NotificationType.INFO) {
    return NotificationType.INFO;
  }
  if (normalized === NotificationType.SUCCESS) {
    return NotificationType.SUCCESS;
  }
  if (normalized === NotificationType.WARNING) {
    return NotificationType.WARNING;
  }
  if (normalized === NotificationType.ERROR) {
    return NotificationType.ERROR;
  }
  if (normalized === NotificationType.FATAL) {
    return NotificationType.FATAL;
  }

  return NotificationType.FATAL;
};

export class ErrorMessageServiceConnector
  extends BaseConnector
  implements IErrorMessageServiceConnector
{
  private static instance: ErrorMessageServiceConnector;
  private static baseUrl = getConfig().api.errorMessageService.url;
  private static scopes = [getConfig().api.errorMessageService.scope];

  public static getInstance(): IErrorMessageServiceConnector {
    if (!ErrorMessageServiceConnector.instance) {
      ErrorMessageServiceConnector.instance = new ErrorMessageServiceConnector();
    }
    return ErrorMessageServiceConnector.instance;
  }

  private constructor() {
    super(
      ErrorMessageServiceConnector.baseUrl,
      ErrorMessageServiceConnector.scopes,
      API_TIMEOUTS.errorMessageService,
    );
  }

  public async getNotificationDetails(code: string): Promise<BaseNotification> {
    const id = uuid();
    try {
      if (!code) {
        return Promise.reject();
      }

      const response = await this.get<ErrorMessageServiceResponse>(
        `/retrieve_error_message/${code}`,
      );

      if (!response) {
        return {
          id,
          timestamp: new Date(),
          read: false,
          title: t('errors:notifications.default.title'),
          description: t('errors:notifications.default.description'),
          solution: '',
          type: NotificationType.FATAL,
          readMoreUrl: '',
          code,
          severity: 'FATAL',
        };
      }

      return {
        id,
        timestamp: new Date(),
        type: toNotificationType(response.severity),
        title: `${response.code}: ${
          response.descriptions[0]?.title ?? t('errors:notifications.default.title')
        }`,
        description:
          response.descriptions[0]?.description ?? t('errors:notifications.default.description'),
        solution: response.descriptions[0]?.solution ?? '',
        readMoreUrl: response.readMoreUrl,
        severity: response.severity,
        read: false,
        code: response.code,
      };
    } catch (error) {
      return {
        id,
        timestamp: new Date(),
        read: false,
        title: t('errors:notifications.default.title'),
        description: t('errors:notifications.default.description'),
        solution: '',
        type: NotificationType.FATAL,
        readMoreUrl: '',
        code,
        severity: 'FATAL',
      };
    }
  }
}
